'use client';

import { useCallback } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Textarea } from '../../components/textarea';
import { Text } from '../../components/styled/text';
import { Image01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  POC_IMAGE_CAPTION_MAX_LENGTH,
  type PocBlock,
} from '@terra/shared-schemas-orchestrator';
import { FindingPocImagePreview } from './FindingPocImagePreview';

type StructuredPocImageBlockEditorProps = {
  block: Extract<
    PocBlock,
    {
      type: 'image';
    }
  >;
  findingId?: string;
  onUpdate: (updated: PocBlock) => void;
};

export function StructuredPocImageBlockEditor({
  block,
  findingId,
  onUpdate,
}: StructuredPocImageBlockEditorProps) {
  const t = useTranslations('designSystem.findings.structuredPocEditor');
  const handleCaptionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const captionValue = event.target.value;

      onUpdate({
        ...block,
        caption:
          captionValue.length === 0
            ? undefined
            : captionValue.slice(0, POC_IMAGE_CAPTION_MAX_LENGTH),
      });
    },
    [block, onUpdate],
  );

  return (
    <Box pr={16} w="full" overflow="hidden">
      <VStack
        gap={2}
        p={3}
        alignItems="stretch"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
      >
        <FindingPocImagePreview
          findingId={findingId}
          imageId={block.imageId}
          alt={block.caption ?? block.fileName}
        />
        <HStack gap={2}>
          <Image01Icon size={14} />
          <Text fontSize="xs" color="fg.muted">
            {block.fileName}
          </Text>
        </HStack>
        <Box>
          <Text fontSize="2xs" color="fg.muted" mb={1}>
            {t('caption')}
          </Text>
          <Textarea
            size="sm"
            value={block.caption ?? ''}
            onChange={handleCaptionChange}
            placeholder={t('captionPlaceholder')}
            rows={2}
            resize="vertical"
          />
        </Box>
      </VStack>
    </Box>
  );
}
